import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useDebounce } from '@/hooks/useDebounce';
import {
  PCMP_PROJECT_LIST_PAGE_SIZE,
  type PcmpProjectActiveFilter,
} from '../constants/pcmpProject.constants';
import type { PcmpProjectListParams } from '../types/pcmpProject.types';

const route = getRouteApi('/_auth/pcmp/projects/');

interface PcmpProjectFilters {
  search: string;
  bookEditionNo: number | undefined;
  companyId: number | undefined;
  activeState: PcmpProjectActiveFilter | undefined;
  activeFrom: string;
  activeTo: string;
}

const INITIAL_FILTERS: PcmpProjectFilters = {
  search: '',
  bookEditionNo: undefined,
  companyId: undefined,
  activeState: undefined,
  activeFrom: '',
  activeTo: '',
};

export function usePcmpProjectFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedFilters = useMemo<PcmpProjectFilters>(
    () => ({
      search: search.q ?? '',
      bookEditionNo: search.edition,
      companyId: search.company,
      activeState: search.state,
      activeFrom: search.from ?? '',
      activeTo: search.to ?? '',
    }),
    [search.q, search.edition, search.company, search.state, search.from, search.to],
  );

  const [draftFilters, setDraftFilters] = useState<PcmpProjectFilters>(appliedFilters);
  const debouncedSearch = useDebounce(draftFilters.search);

  useEffect(() => {
    setDraftFilters(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    const trimmedDebounced = debouncedSearch.trim();
    if (trimmedDebounced === appliedFilters.search.trim()) return;
    void navigate({
      search: (prev) => ({ ...prev, q: trimmedDebounced || undefined }),
      replace: true,
    });
  }, [debouncedSearch, appliedFilters.search, navigate]);

  const hasActiveFilters =
    appliedFilters.search !== '' ||
    appliedFilters.bookEditionNo !== undefined ||
    appliedFilters.companyId !== undefined ||
    appliedFilters.activeState !== undefined ||
    appliedFilters.activeFrom !== '' ||
    appliedFilters.activeTo !== '';

  const isDateRangeInvalid =
    draftFilters.activeFrom !== '' &&
    draftFilters.activeTo !== '' &&
    draftFilters.activeFrom > draftFilters.activeTo;

  const listQueryParams = useMemo<Omit<PcmpProjectListParams, 'page'>>(
    () => ({
      search: appliedFilters.search.trim() || undefined,
      bookEditionNo: appliedFilters.bookEditionNo,
      companyId: appliedFilters.companyId,
      isActive:
        appliedFilters.activeState === undefined
          ? undefined
          : appliedFilters.activeState === 'active',
      activeFrom: appliedFilters.activeFrom || undefined,
      activeTo: appliedFilters.activeTo || undefined,
      limit: PCMP_PROJECT_LIST_PAGE_SIZE,
    }),
    [appliedFilters],
  );

  const setDraft = useCallback(
    <K extends keyof PcmpProjectFilters>(key: K, value: PcmpProjectFilters[K]) => {
      setDraftFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSearchChange = useCallback((value: string) => setDraft('search', value), [setDraft]);

  const handleApply = useCallback(() => {
    void navigate({
      search: {
        q: draftFilters.search.trim() || undefined,
        edition: draftFilters.bookEditionNo,
        company: draftFilters.companyId,
        state: draftFilters.activeState,
        from: draftFilters.activeFrom || undefined,
        to: draftFilters.activeTo || undefined,
      },
    });
  }, [draftFilters, navigate]);

  const handleReset = useCallback(() => {
    setDraftFilters(INITIAL_FILTERS);
    void navigate({ search: {} });
  }, [navigate]);

  return {
    filters: draftFilters,
    appliedFilters,
    hasActiveFilters,
    isDateRangeInvalid,
    listQueryParams,
    setDraft,
    handleSearchChange,
    handleApply,
    handleReset,
  };
}
